import axios from 'axios';
import { API_BASE } from '../../../scripts/utils/api';

const createCisMemberForm = document.getElementById('create-cis-member');
const responseMessageElement = document.getElementById('create-cis-member-response');

const SPINNER = '<svg class="animate-spin h-7 w-7 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

const STATUS_STYLES = {
  loading: 'border-grey-300 bg-grey-100',
  success: 'border-green-600 bg-green-50',
  warning: 'border-amber-500 bg-amber-50',
  error: 'border-red-600 bg-red-50',
};

let translations = {};
try {
  translations = JSON.parse(document.getElementById('create-cis-member-translations')?.textContent || '{}');
} catch (error) {
  console.error('Failed to parse CIS member translations:', error);
}

function t(path) {
  const value = path.split('.').reduce((node, key) => node?.[key], translations);
  return typeof value === 'string' ? value : path;
}

const KNOWN_ERRORS = [
  { match: /phone has already been taken/i, key: 'phone_taken' },
  { match: /email has already been taken/i, key: 'email_taken' },
  { match: /email (is invalid|contains)/i, key: 'email_invalid' },
  { match: /phone is invalid/i, key: 'phone_invalid' },
];

function errorMessage(key, detail) {
  return { title: t(`errors.${key}.title`), text: t(`errors.${key}.text`), detail };
}

function describeRejection(message = '') {
  const known = KNOWN_ERRORS.find(({ match }) => match.test(message));
  if (known) return errorMessage(known.key);

  return errorMessage('unknown', message.replace(/^customerCreate rejected:\s*/i, ''));
}

function describeError(error) {
  if (!error?.response) return errorMessage('network', error?.message);

  const message = error.response.data?.message || '';
  if (error.response.status >= 500) return errorMessage('server', message || `HTTP ${error.response.status}`);

  return describeRejection(message);
}

// DOM nodes, not innerHTML: messages contain form input and API text.
function renderStatus(state, { title, text, lines = [], detail } = {}) {
  responseMessageElement.className = `rounded border-2 p-6 flex flex-col gap-3 ${STATUS_STYLES[state]}`;
  responseMessageElement.replaceChildren();

  const heading = document.createElement('div');
  heading.className = 'flex items-center gap-3 text-2xl font-bold';
  if (state === 'loading') heading.insertAdjacentHTML('afterbegin', SPINNER);
  heading.append(title);
  responseMessageElement.append(heading);

  if (text) {
    const paragraph = document.createElement('p');
    paragraph.className = 'text-lg';
    paragraph.textContent = text;
    responseMessageElement.append(paragraph);
  }

  if (lines.length) {
    const list = document.createElement('ul');
    list.className = 'flex flex-col gap-1 text-lg';
    lines.forEach(({ ok, text: lineText }) => {
      const item = document.createElement('li');
      item.textContent = `${ok ? '✓' : '✗'} ${lineText}`;
      list.append(item);
    });
    responseMessageElement.append(list);
  }

  if (detail) {
    const detailElement = document.createElement('p');
    detailElement.className = 'text-sm text-grey-700 break-words';
    detailElement.textContent = `${t('error_detail')}: ${detail}`;
    responseMessageElement.append(detailElement);
  }
}

let isSubmitting = false;

if (createCisMemberForm) {
  createCisMemberForm.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    isSubmitting = true;
    const submitButton = createCisMemberForm.querySelector('[type="submit"]');
    submitButton?.setAttribute('disabled', '');
    renderStatus('loading', { title: t('loading') });

    const formData = new FormData(createCisMemberForm);

    const createShopifyCustomerData = {
      firstName: formData.get('first_name'),
      lastName: formData.get('last_name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      taxExempt: false,
      accepts_marketing: true,
      smsMarketingConsent: {
        marketingState: 'UNSUBSCRIBED',
        marketingOptInLevel: 'UNKNOWN',
      },
      emailMarketingConsent: {
        marketingState: 'SUBSCRIBED',
        marketingOptInLevel: 'UNKNOWN',
      },
      fav_club: formData.get('club_search_input'),
      fav_store: formData.get('store_search_input'),
    };

    let step = 'create';
    try {
      const shopifyResponse = await axios.post(`${API_BASE}/createCustomer`, createShopifyCustomerData);

      const userErrors = shopifyResponse?.data?.customer?.data?.customerCreate?.userErrors;
      if (userErrors?.length) {
        renderStatus('error', describeRejection(userErrors.map((error) => error.message).join('; ')));
        return;
      }

      step = 'tag';
      await axios.post(`${API_BASE}/updateCustomerTags`, { ...createShopifyCustomerData, extraTags: ['cis_from_store'] });

      step = 'status';
      const customerId = shopifyResponse?.data?.customer?.data?.customerCreate?.customer?.id?.split('/').pop();
      const shopifyCustomer = await axios.get(`${API_BASE}/getCustomer?id=${customerId}`);
      const tags = (shopifyCustomer?.data?.tags || '').toLowerCase().split(',').map((tag) => tag.trim());
      const isMember = tags.includes('club_intersport');

      const lines = [
        { ok: true, text: t('steps.shopify_created') },
        { ok: isMember, text: t(isMember ? 'steps.member_tagged' : 'steps.member_not_tagged') },
      ];
      const allGood = lines.every((line) => line.ok);
      const outcome = allGood ? 'success' : 'partial';

      renderStatus(allGood ? 'success' : 'warning', {
        title: t(`${outcome}.title`),
        text: t(`${outcome}.text`),
        lines,
      });

      createCisMemberForm.reset();
    } catch (error) {
      console.log('🚀 ~ error:', error, error?.response?.data?.message);

      if (step === 'create') {
        renderStatus('error', describeError(error));
        return;
      }

      const tagged = step === 'status';
      const outcome = tagged ? 'status_failed' : 'tag_failed';
      renderStatus('warning', {
        title: t(`${outcome}.title`),
        text: t(`${outcome}.text`),
        lines: [
          { ok: true, text: t('steps.shopify_created') },
          { ok: tagged, text: t(tagged ? 'steps.member_tagged' : 'steps.member_tag_failed') },
        ],
        detail: error?.response?.data?.message || error?.message,
      });
      createCisMemberForm.reset();
    } finally {
      isSubmitting = false;
      submitButton?.removeAttribute('disabled');
    }
  });
}
